import React from 'react'
import { useQuery, gql } from '@apollo/client'
import { useTranslation } from 'react-i18next'

import Loader from '../components/loader'
import { PageBody } from '../components/page-body'

import useGetCategoryName from '../hooks/useGetCategoryName'
import useGetSystemName from '../hooks/useGetSystemName'

import {
  ExclamationIcon,
  UserIcon,
  UserAddIcon,
  PencilIcon
} from '@heroicons/react/solid'

const QUERY = gql`
  query Users {
    appAdminUsers {
      id
      email
      appAccepted
      systemsV2 {
        id
        defaultCategory
        systemSiteData {
          systemName
          locale
        }
      }
      appLocales
      appRole
      vendor {
        id
        company
        accessRights
        systemsV2 {
          id
          defaultCategory
          systemSiteData {
            locale
            systemName
          }
        }
      }
    }
  }
`

function User({ user }) {
  const { t } = useTranslation()
  const { getCategoryName } = useGetCategoryName()
  const { getSystemName } = useGetSystemName()
  const hasAccepted = user.appAccepted

  let icon
  switch (user?.appRole) {
    case 'USER':
      icon = (
        <UserIcon className="h-10 w-10 rounded-full text-gray-400" aria-hidden="true" />
      )
      break

    case 'EDITOR':
      icon = (
        <PencilIcon
          className="h-10 w-10 rounded-full text-emerald-400"
          aria-hidden="true"
        />
      )
      break

    case 'ADMIN':
      icon = (
        <UserAddIcon
          className="h-10 w-10 rounded-full text-blue-400"
          aria-hidden="true"
        />
      )
      break
    default:
      icon = (
        <ExclamationIcon
          className="h-10 w-10 rounded-full text-yellow-400"
          aria-hidden="true"
        />
      )
      break
  }

  return (
    <tr key={user.email}>
      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
        <div className="flex items-center">
          <div className="h-10 w-10 flex-shrink-0">{icon}</div>
          <div className="ml-4">
            <div className="font-medium text-gray-900">{user.email}</div>
            <div className="text-gray-500">{user.vendor?.company}</div>
          </div>
        </div>
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        <div className="text-gray-900">
          {user.vendor?.accessRights.map((right) => {
            return <div key={right}>{right}</div>
          })}
        </div>
        <div className="border-t-4 text-gray-500">{user?.appRole}</div>
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        {user.appLocales.map((region) => {
          return <div key={region}>{region}</div>
        })}
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        {user.systemsV2?.map((system) => {
          return (
            <div key={system.id} style={{ display: 'flex', whiteSpace: 'pre' }}>
              {getSystemName(system)} -{' '}
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {getCategoryName(system.defaultCategory, true)}
              </span>
            </div>
          )
        })}
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        {hasAccepted ? (
          <span className="inline-flex rounded-full bg-green-100 px-2 text-xs font-semibold leading-5 text-green-800">
            {t('content:active')}
          </span>
        ) : (
          <span className="inline-flex rounded-full bg-red-100 px-2 text-xs font-semibold leading-5 text-red-800">
            {t('content:inactive')}
          </span>
        )}
      </td>
    </tr>
  )
}

function AppUsers({ appUsers }) {
  const { t } = useTranslation()

  if (!appUsers?.length) {
    return <div>{t('content:warning.noData')}</div>
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="mt-8 flex flex-col">
        <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                    >
                      {t('content:name')}
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      {t('content:privileges')}
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      {t('content:markets')}
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      {t('content:system')}
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      {t('content:status')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {appUsers
                    .slice()
                    .sort((a, b) => {
                      const nameA = a.vendor?.company.toUpperCase()
                      const nameB = b.vendor?.company.toUpperCase()
                      if (nameA < nameB) {
                        return -1
                      }
                      if (nameA > nameB) {
                        return 1
                      }

                      return 0
                    })
                    .map((user) => {
                      return <User key={user.id} user={user} />
                    })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function AdminAllUsersPage() {
  const { data, loading, error } = useQuery(QUERY)

  if (error) {
    console.error(error)
  }

  const appUsers = data?.appAdminUsers

  return (
    <PageBody>
      {error &&
        error.graphQLErrors.map(({ message }, i) => <span key={i}>{message}</span>)}
      {loading && (
        <div className="text-center">
          <Loader />
        </div>
      )}
      <div>{!loading && <AppUsers appUsers={appUsers} />}</div>
    </PageBody>
  )
}

export default AdminAllUsersPage
